import { FC, useCallback, useMemo } from 'react';
import { UserEventMessage } from '@/lib/models/user-event';
import MoreMenu from '@/components/more-menu';
import CopyMenuItem from '@/components/more-menu/copy-menu-item';
import MenuItem from '@/components/menu/menu-item';
import {
  getPeerToolMessageElementId,
  getToolUseIdFromMessage,
} from '@/lib/models/user-event/tools';

interface Props {
  message: UserEventMessage;
  traceId?: string;
  hasToolUsePair: boolean;
}

const MessageMenu: FC<Props> = ({ message, traceId, hasToolUsePair }) => {
  const { content, messageId, requestId, timestamp, role } = message;

  const copyableContent = content.trim();
  const hasCopyableContent = !!copyableContent.length;

  const menuId = useMemo(
    () => `conversation-message-menu-${messageId ?? requestId}-${timestamp}`,
    [messageId, requestId, timestamp]
  );
  const toolUseId = getToolUseIdFromMessage(message);
  const hasToolUseId = !!toolUseId;

  const handleJumpToPair = useCallback(() => {
    if (!hasToolUseId) {
      return;
    }
    const peerElementId = getPeerToolMessageElementId(role, toolUseId);
    if (!peerElementId) {
      return;
    }
    document.getElementById(peerElementId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [hasToolUseId, role, toolUseId]);

  return (
    <MoreMenu hideCopy menuId={menuId} color="grey">
      {hasCopyableContent && (
        <CopyMenuItem label="Copy message content" icon="copy" value={copyableContent} />
      )}
      {messageId && (
        <CopyMenuItem
          label="Copy Message ID"
          icon="copy-id"
          value={messageId}
          subtitle={messageId}
        />
      )}
      {traceId && (
        <CopyMenuItem label="Copy Trace ID" icon="copy-id" value={traceId} subtitle={traceId} />
      )}
      {hasToolUseId && (
        <CopyMenuItem
          label="Copy Tool Use ID"
          icon="copy-id"
          value={toolUseId}
          subtitle={toolUseId}
        />
      )}
      {hasToolUsePair && (
        <MenuItem
          label={`Jump to tool ${role === 'tool_use' ? 'response' : 'selection'}`}
          icon="arrow-right"
          onClick={handleJumpToPair}
        />
      )}
    </MoreMenu>
  );
};

export default MessageMenu;
